"use client";
import InpSafeForm from "@/components/atoms/FormComponents/InpSafeForm";

import { Controller } from "react-hook-form";
import Checkbox from "@/components/atoms/FormComponents/Checkbox";
import Input from "@/components/atoms/FormComponents/Input/Input";
import SubmitButton from "@/components/atoms/FormComponents/SubmitButton";
import cn from "clsx";
import styles from "../ConnectModalForm/ConnectModalForm.module.scss";
import ApplicationFormRedirectLoader from "../../ApplicationFormRedirectLoader/ApplicationFormRedirectLoader";
import ConsentFormPara from "@/components/common/utils/consentFormPara";
import CountryDropdownGlobal from "@/components/atoms/FormComponents/CustomDropdownGlobal/CustomDropdownGlobal";
import ThankyouForInterestSection from "../LandingPageConnectModalForm/Thankyou";
import { ALL_COUNTRIES } from "@/context/countries";
import { useProgramPageConnectModalFormController } from "./controller/useProgramPageConnectModalFormController";

const getCssConfig = ({ isNewLeadForm = false }) => ({
  formWrapper: isNewLeadForm ? `${styles.newLeadFormWrapper}` : "h-[100%]",
  firstName: isNewLeadForm
    ? `${styles.newLeadFormFirstName}`
    : cn(styles.inputWrapper, "flex gap-4"),
  phoneNumber: isNewLeadForm
    ? `${styles.newLeadFormPhone}`
    : cn(styles.commonMargins),
  email: isNewLeadForm
    ? `${styles.newLeadFormEmail}`
    : cn(styles.commonMargins, styles.emailContainer, "flex gap-4"),
  termsConditions: isNewLeadForm
    ? cn(styles.commonMargins, "flex items-start gap-[17px] mt-4")
    : cn(styles.commonMargins, "flex items-start gap-[17px]"),
});

export default function ProgramPageConnectModalForm(props: {
  ProgramId?: any;
  associatedPrograms: Array<ProgramDetails>;
  isNewLeadForm?: boolean;
  NewBannerUI?: NewBannerUI;
  isMobile?: boolean;
  setLeadFailed?: (x: boolean) => void;
  isSeatsFilledEnabled: boolean;
  Numbers: number;
  isProgramPage?: boolean;
}) {
  const {
    control,
    errors,
    isSubmitting,
    isOTPLoading,
    isApplicationRedirectLoaderDisabled,
    isThankYouBannerHidden,
    loadingCount,
    message,
    isNewLeadForm,
    isNational,
    isCertification,
    btnColors,
    isSeatsFilledEnabled,
    Numbers,
    setRefs,
    handleFieldChange,
    handlePhoneNumberChange,
    handleFieldBlur,
    updateCountryCode,
    handlePreDegree,
    handleFormSubmit,
  } = useProgramPageConnectModalFormController(props);

  const cssConfig = getCssConfig({ isNewLeadForm });
  const isLoading = isSubmitting;

  return (
    <>
      {!isApplicationRedirectLoaderDisabled ? (
        <ApplicationFormRedirectLoader redirectTime={loadingCount} />
      ) : !isThankYouBannerHidden ? (
        <ThankyouForInterestSection />
      ) : (
        <InpSafeForm
          formRef={setRefs}
          onSubmit={handleFormSubmit}
          className={cn(styles.root, "h-full flex flex-col justify-between	")}
          onFocus={handlePreDegree}
        >
          <div className={cssConfig.formWrapper}>
            <div className={cssConfig.firstName}>
              <div className="w-[100%]">
                <label htmlFor="fullName">
                  full name <span className="text-xl">*</span>
                </label>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Enter your full name"
                      value={field.value ?? ""}
                      onChangeFunc={(value: string) =>
                        handleFieldChange("fullName", value)
                      }
                      onBlur={() =>
                        handleFieldBlur("fullName", "", errors.fullName)
                      }
                      disabled={isLoading || isOTPLoading}
                      id="fullName"
                    />
                  )}
                />
                <p className={cn(styles.error)}>{errors.fullName}</p>
              </div>
            </div>
            <div className={cssConfig.phoneNumber}>
              <div className="w-[100%]">
                <label htmlFor="phoneNumber">
                  phone number <span className="text-xl">*</span>
                </label>
                <div className="flex gap-[6px]">
                  <div className="md:mt-[0.36rem] sm:mt-[0.3rem] lg:mt-[0.4166666667rem]">
                    <Controller
                      name="countryCode"
                      control={control}
                      render={({ field }) => (
                        <CountryDropdownGlobal
                          onChangeFunc={(value: string) =>
                            handleFieldChange("countryCode", value)
                          }
                          countries={ALL_COUNTRIES}
                          countryCode={field.value}
                          updateCountryCode={updateCountryCode}
                          additionalStyle="md:h-[2.6rem] h-[2.75rem] lg:h-[2.25rem]"
                          isDark={false}
                          disabled={isLoading || isOTPLoading}
                        />
                      )}
                    />
                  </div>
                  <div className="w-full">
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter your no."
                          value={field.value ?? ""}
                          onChangeFunc={handlePhoneNumberChange}
                          onBlur={() =>
                            handleFieldBlur(
                              "phoneNumber",
                              "",
                              errors.phoneNumber,
                            )
                          }
                          disabled={isLoading || isOTPLoading}
                          id="phoneNumber"
                          isTel
                        />
                      )}
                    />
                  </div>
                </div>
                <p className={cn(styles.error)}>
                  {errors.countryCode || errors.phoneNumber}
                </p>
              </div>
            </div>

            <div
              className={
                isNational && isCertification
                  ? cssConfig.email
                  : styles.newLeadFormEmailFullWidth
              }
            >
              <div className="w-full">
                <label htmlFor="emailId">
                  email id <span className="text-xl">*</span>
                </label>
                <Controller
                  name="emailId"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="abc@xyz.com"
                      value={field.value ?? ""}
                      onChangeFunc={(value: string) =>
                        handleFieldChange("emailId", value)
                      }
                      onBlur={() =>
                        handleFieldBlur("emailId", "", errors.emailId)
                      }
                      disabled={isLoading || isOTPLoading}
                      id="emailId"
                    />
                  )}
                />
                <p className={cn(styles.error)}>{errors.emailId}</p>
              </div>
            </div>
          </div>

          <div className={cssConfig.termsConditions}>
            <div className="shrink-0">
              <Controller
                name="isTermsAccepted"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    name="consent"
                    id="is_terms_accepted"
                    value={field.value}
                    onChangeFunc={(value: boolean) =>
                      handleFieldChange("isTermsAccepted", value)
                    }
                    onBlur={() =>
                      handleFieldBlur(
                        "isTermsAccepted",
                        "",
                        errors?.isTermsAccepted || "NA",
                      )
                    }
                    disabled={isLoading || isOTPLoading}
                  />
                )}
              />
            </div>
            <p className={cn(styles.authorization)}>
              <ConsentFormPara />
            </p>
          </div>

          <SubmitButton
            id="submitButton"
            text={isLoading || isOTPLoading ? "Submitting" : "Submit"}
            isDisabled={isLoading || isOTPLoading}
            bgColor={btnColors.bg}
            textColor={btnColors.color}
          />
          {message && message}
          {isSeatsFilledEnabled && (
            <div
              style={{
                width: "100%",
                backgroundColor: "#e0e0e0",
                borderRadius: "20px",
                overflow: "hidden",
                position: "relative",
                marginTop: "20px",
              }}
            >
              <div
                style={{
                  width: `${Numbers}%`,
                  backgroundColor: "#1275d8",
                  height: "30px",
                  transition: "width 0.3s ease",
                  animation: "progress-bar-slide 2s linear infinite",
                  backgroundImage:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent)",
                  backgroundSize: "30px 30px",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    zIndex: 2,
                  }}
                >
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      color: "#FFFFFF",
                      textShadow: "1px 1px 2px rgba(0, 0, 0, 0.7)",
                    }}
                  >
                    {Numbers}%
                  </span>
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: "14px",
                      marginLeft: "5px",
                      color: "#FFFFFF",
                      textShadow: "1px 1px 2px rgba(0, 0, 0, 0.7)",
                    }}
                  >
                    Seats filled
                  </span>
                </div>
              </div>
            </div>
          )}
          {!isSeatsFilledEnabled && (
            <div style={{ marginBottom: "0rem" }}></div>
          )}

          <style jsx>{`
            @keyframes progress-bar-slide {
              from {
                background-position: 0 0;
              }
              to {
                background-position: 30px 0;
              }
            }
          `}</style>
        </InpSafeForm>
      )}
    </>
  );
}
